"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AccountDeletionSchema = exports.WebAuthnCredential = exports.WebAuthnAuthenticateCompleteSchema = exports.WebAuthnAuthenticateInitiateSchema = exports.WebAuthnRegisterCompleteSchema = exports.WebAuthnRegisterInitiateSchema = exports.OAuthUserProfile = exports.ChangePasswordSchema = exports.PasswordResetSchema = exports.PasswordResetRequestSchema = exports.MFAVerifySchema = exports.MFASetupSchema = exports.AppInviteSchema = exports.MagicLinkVerifySchema = exports.MagicLinkRequestSchema = exports.EmailLoginSchema = exports.PhoneRegistrationSchema = exports.EmailRegistrationSchema = exports.UserSchema = exports.IdentityWalletSchema = exports.VerifiedClaimSchema = exports.IdentityProvider = exports.IdentityVerificationSchema = exports.IdentityVerificationStatus = exports.IdentityVerificationMethod = exports.UserTier = exports.NumericToProfileVisibility = exports.ProfileVisibilityNumeric = exports.ProfileVisibility = exports.LoginMethodStat = exports.LoginMethod = exports.OAuthProvider = exports.UserStatus = exports.UserRole = exports.WebAuthnCredentialSchema = exports.WebAuthnAuthenticationResponseSchema = exports.WebAuthnCredentialResponseSchema = exports.MFATokenSchema = exports.AcceptTermsSchema = exports.PhoneNumberSchema = exports.UsernameSchema = exports.EmailSchema = exports.LoginPasswordSchema = exports.StrongPasswordSchema = exports.DeviceInfoSchema = void 0;
const zod_1 = require("zod");
// Shared base schemas
exports.DeviceInfoSchema = zod_1.z
    .object({
    deviceName: zod_1.z.string().max(100).optional(),
    userAgent: zod_1.z.string().max(500).optional(),
    deviceType: zod_1.z.enum(["desktop", "mobile", "tablet", "unknown"]).optional(),
    platform: zod_1.z.string().optional(),
    browser: zod_1.z.string().optional(),
    ipAddress: zod_1.z.string().optional(),
    fingerprint: zod_1.z.string().optional(),
})
    .optional();
exports.StrongPasswordSchema = zod_1.z
    .string()
    .min(8)
    .max(128)
    .regex(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]*$/, "Password must contain uppercase, lowercase, digit and special character");
exports.LoginPasswordSchema = zod_1.z.string().max(128);
exports.EmailSchema = zod_1.z.string().email().max(320);
exports.UsernameSchema = zod_1.z
    .string()
    .min(3)
    .max(30)
    .regex(/^[a-zA-Z0-9_]+$/, "Username can only contain letters, numbers, and underscores");
exports.PhoneNumberSchema = zod_1.z
    .string()
    .regex(/^\+[1-9]\d{4,13}$/, "Invalid E.164 phone format");
exports.AcceptTermsSchema = zod_1.z
    .boolean()
    .refine((val) => val === true, "Must accept terms");
exports.MFATokenSchema = zod_1.z
    .string()
    .min(6)
    .max(6)
    .regex(/^\d{6}$/, "Token must be exactly 6 digits");
// WebAuthn credential schemas
exports.WebAuthnCredentialResponseSchema = zod_1.z.object({
    attestationObject: zod_1.z.string(),
    clientDataJSON: zod_1.z.string(),
});
exports.WebAuthnAuthenticationResponseSchema = zod_1.z.object({
    authenticatorData: zod_1.z.string(),
    clientDataJSON: zod_1.z.string(),
    signature: zod_1.z.string(),
    userHandle: zod_1.z.string().optional(),
});
exports.WebAuthnCredentialSchema = zod_1.z.object({
    id: zod_1.z.string(),
    rawId: zod_1.z.string(),
    type: zod_1.z.literal("public-key"),
});
exports.UserRole = zod_1.z.enum(["user", "admin", "service", "banned"]);
exports.UserStatus = zod_1.z.enum([
    "active",
    "inactive",
    "pending",
    "locked",
    "archived",
]);
exports.OAuthProvider = zod_1.z.enum([
    "google",
    "facebook",
    "apple",
    "github",
    "slack",
    "atproto",
]);
/** Sign-in methods tracked per account for usage stats / recommendations. */
exports.LoginMethod = zod_1.z.enum([
    "password",
    "magic-link",
    "phone",
    "passkey",
    "oauth",
]);
/** Per-method usage stat stored on the user record. */
exports.LoginMethodStat = zod_1.z.object({
    count: zod_1.z.number(),
    lastUsedAt: zod_1.z.string().datetime(),
});
// Profile visibility levels (stored as numbers in DB for performance)
exports.ProfileVisibility = zod_1.z.enum(["private", "registered", "everyone"]);
// Numeric mapping for database storage
exports.ProfileVisibilityNumeric = {
    private: 0,
    registered: 1,
    everyone: 2,
};
exports.NumericToProfileVisibility = {
    0: "private",
    1: "registered",
    2: "everyone",
};
exports.UserTier = zod_1.z.number().int().min(0).max(6);
// Legacy identity verification (single Holonym proof) — kept for backward compat
exports.IdentityVerificationMethod = zod_1.z.enum(["gov-id", "phone", "epassport"]);
exports.IdentityVerificationStatus = zod_1.z.enum(["none", "pending", "verified"]);
exports.IdentityVerificationSchema = zod_1.z.object({
    status: exports.IdentityVerificationStatus.default("none"),
    method: exports.IdentityVerificationMethod.optional(),
    verifiedAt: zod_1.z.string().datetime().optional(),
    proof: zod_1.z.unknown().optional(),
});
// Multi-provider identity credential wallet
//
// `self` is the odd one out: it records an assertion the user makes rather
// than a proof they supply, for facts with no incentive to falsify (holding a
// licensed copy of a freely-available specification, say). It must never gate
// anything a false assertion would unfairly obtain — see the guidance on
// `selfProvider` in apps/api/auth/lib/identity-providers.ts.
exports.IdentityProvider = zod_1.z.enum(["holonym", "reclaim", "worldid", "kilt", "self"]);
exports.VerifiedClaimSchema = zod_1.z.object({
    id: zod_1.z.string().uuid(),
    provider: exports.IdentityProvider,
    claimType: zod_1.z.string(),
    label: zod_1.z.string(),
    claims: zod_1.z.record(zod_1.z.unknown()),
    proof: zod_1.z.unknown().optional(),
    verifiedAt: zod_1.z.string().datetime(),
    disclosed: zod_1.z.boolean().default(false),
});
exports.IdentityWalletSchema = zod_1.z.object({
    claims: zod_1.z.array(exports.VerifiedClaimSchema).default([]),
    updatedAt: zod_1.z.string().datetime().optional(),
});
exports.UserSchema = zod_1.z.object({
    id: zod_1.z.string().uuid(),
    email: zod_1.z.string().email(),
    emailVerified: zod_1.z.boolean().default(false),
    phoneNumber: zod_1.z.string().optional(),
    phoneVerified: zod_1.z.boolean().default(false),
    passwordHash: zod_1.z.string().optional(),
    username: exports.UsernameSchema,
    avatarUrl: zod_1.z.string().url().optional(),
    initials: zod_1.z.string().max(3).optional(),
    avatarBgColor: zod_1.z.string().max(20).optional(),
    backgroundUrl: zod_1.z.string().url().optional(),
    visibility: exports.ProfileVisibility.default("private"),
    role: exports.UserRole.default("user"),
    scopes: zod_1.z.array(zod_1.z.string()).default([]),
    tier: exports.UserTier.default(0),
    planId: zod_1.z.string().optional(),
    referralCode: zod_1.z.string().optional(),
    referredBy: zod_1.z.string().optional(),
    expiresAt: zod_1.z.string().datetime().optional(),
    status: exports.UserStatus.default("pending"),
    // Snapshot of role/scopes/tier before banning — used to restore on unban
    bannedFrom: zod_1.z.object({
        role: zod_1.z.enum(["user", "admin", "service"]),
        scopes: zod_1.z.array(zod_1.z.string()),
        tier: exports.UserTier,
        bannedAt: zod_1.z.string().datetime(),
    }).optional(),
    mfaEnabled: zod_1.z.boolean().default(false),
    mfaSecret: zod_1.z.string().optional(),
    webauthnEnabled: zod_1.z.boolean().default(false),
    identityVerification: exports.IdentityVerificationSchema.optional(),
    identityWallet: exports.IdentityWalletSchema.optional(),
    atprotoConnection: zod_1.z.object({
        did: zod_1.z.string(),
        handle: zod_1.z.string(),
        pdsUrl: zod_1.z.string().url(),
        connectedAt: zod_1.z.string().datetime(),
    }).optional(),
    oauthProviders: zod_1.z.array(exports.OAuthProvider).default([]),
    loginAttempts: zod_1.z.number().default(0),
    lockedUntil: zod_1.z.string().datetime().optional(),
    lastLoginAt: zod_1.z.string().datetime().optional(),
    createdAt: zod_1.z.string().datetime(),
    updatedAt: zod_1.z.string().datetime(),
});
exports.EmailRegistrationSchema = zod_1.z.object({
    email: exports.EmailSchema,
    password: exports.StrongPasswordSchema,
    username: exports.UsernameSchema,
    acceptTerms: exports.AcceptTermsSchema,
    referralCode: zod_1.z.string().max(20).optional(),
    deviceInfo: exports.DeviceInfoSchema,
});
exports.PhoneRegistrationSchema = zod_1.z.object({
    phoneNumber: exports.PhoneNumberSchema,
    username: exports.UsernameSchema,
    acceptTerms: exports.AcceptTermsSchema,
    referralCode: zod_1.z.string().max(20).optional(),
    deviceInfo: exports.DeviceInfoSchema,
});
exports.EmailLoginSchema = zod_1.z.object({
    email: exports.EmailSchema,
    password: exports.LoginPasswordSchema,
    rememberMe: zod_1.z.boolean().default(false),
    deviceInfo: exports.DeviceInfoSchema,
});
exports.MagicLinkRequestSchema = zod_1.z.object({
    email: exports.EmailSchema,
    username: exports.UsernameSchema.optional(),
    redirectUrl: zod_1.z.string().url().optional(),
    // When true, the verify step will provision an account if none exists yet
    // (the "register with magic link" flow). Plain login requests omit this.
    register: zod_1.z.boolean().optional(),
    deviceInfo: exports.DeviceInfoSchema,
});
exports.MagicLinkVerifySchema = zod_1.z.object({
    token: zod_1.z.string(),
    deviceInfo: exports.DeviceInfoSchema,
});
// Generic app invite (tdd-0050). Any app can invite someone to it by email; the
// emailed link is a magic link that signs the recipient in (bootstrapping an
// account from their email if needed) and redirects into the inviting app.
exports.AppInviteSchema = zod_1.z.object({
    email: exports.EmailSchema,
    /** Display name of the inviting app, e.g. "Messaging". */
    appName: zod_1.z.string().min(1).max(80),
    /** Where to land the invitee after they sign in (app deep-link). */
    appUrl: zod_1.z.string().url(),
    /** Who is inviting them (for the email body). */
    inviterName: zod_1.z.string().min(1).max(120).optional(),
    /** Optional custom plain-text message from the inviter. */
    body: zod_1.z.string().max(1000).optional(),
});
exports.MFASetupSchema = zod_1.z.object({
    method: zod_1.z.enum(["totp", "sms"]),
    phoneNumber: exports.PhoneNumberSchema.optional(),
});
exports.MFAVerifySchema = zod_1.z.object({
    token: exports.MFATokenSchema,
    method: zod_1.z.enum(["totp", "sms"]),
});
exports.PasswordResetRequestSchema = zod_1.z.object({
    email: exports.EmailSchema,
});
exports.PasswordResetSchema = zod_1.z.object({
    token: zod_1.z.string(),
    newPassword: exports.StrongPasswordSchema,
});
exports.ChangePasswordSchema = zod_1.z.object({
    currentPassword: exports.LoginPasswordSchema,
    newPassword: exports.StrongPasswordSchema,
});
exports.OAuthUserProfile = zod_1.z.object({
    id: zod_1.z.string(),
    email: zod_1.z.string().email().optional(),
    username: zod_1.z.string().optional(),
    name: zod_1.z.string().optional(),
    avatarUrl: zod_1.z.string().url().optional(),
    provider: exports.OAuthProvider,
});
exports.WebAuthnRegisterInitiateSchema = zod_1.z.object({
    username: zod_1.z.string().optional(),
    displayName: zod_1.z.string().optional(),
    deviceInfo: exports.DeviceInfoSchema,
});
exports.WebAuthnRegisterCompleteSchema = zod_1.z.object({
    credential: exports.WebAuthnCredentialSchema.extend({
        response: exports.WebAuthnCredentialResponseSchema,
    }),
    challengeId: zod_1.z.string(),
    username: zod_1.z.string().optional(),
});
exports.WebAuthnAuthenticateInitiateSchema = zod_1.z.object({
    username: zod_1.z.string().optional(),
    email: zod_1.z.string().email().optional(),
    deviceInfo: exports.DeviceInfoSchema,
});
exports.WebAuthnAuthenticateCompleteSchema = zod_1.z.object({
    credential: exports.WebAuthnCredentialSchema.extend({
        response: exports.WebAuthnAuthenticationResponseSchema,
    }),
    challengeId: zod_1.z.string(),
    deviceInfo: exports.DeviceInfoSchema,
});
exports.WebAuthnCredential = zod_1.z.object({
    id: zod_1.z.string(),
    credentialId: zod_1.z.string(),
    publicKey: zod_1.z.string(),
    counter: zod_1.z.number(),
    deviceType: zod_1.z.string().optional(),
    deviceName: zod_1.z.string().optional(),
    userId: zod_1.z.string(),
    createdAt: zod_1.z.string().datetime(),
    lastUsedAt: zod_1.z.string().datetime().optional(),
});
exports.AccountDeletionSchema = zod_1.z.object({
    email: exports.EmailSchema,
    phoneNumber: exports.PhoneNumberSchema,
    password: exports.StrongPasswordSchema,
    username: exports.UsernameSchema,
    deviceInfo: exports.DeviceInfoSchema,
});
